import rollbar from 'shared-assets/src/errors/errors';
import featureToggles from 'shared-assets/lib/sdk/featureToggles';
import {
  hasMountedNavContent,
  hideLegacyNavigation,
  mountNavigationUiWithFallback,
  remountNavigationUi,
  showLegacyNavigation,
} from './navigation-v2-loader';

const NAV_ID = 'nav-v2';
const RECOVERY_DELAY_MS = 300;
const MAX_MOUNT_ATTEMPTS = 3;

const STATUS = {
  // Current route explicitly should not show any nav. This is different from
  // feature-flag fallback: no-nav routes should not show legacy chrome either.
  NO_NAV_ROUTE: 'no-nav-route',
  MOUNTING: 'mounting',
  MOUNTED: 'mounted',
  // Feature flag disabled. Legacy chrome is expected and we should not retry
  // on every route event until the page reloads or initNavigationV2 is called.
  FEATURE_SKIPPED: 'feature-skipped',
  // v2 failed on a route that should have nav. Legacy chrome is the fallback.
  FAILED: 'failed',
  RECOVERING: 'recovering',
};

const SKIPPED_HANDLE = {
  mounted: false,
  navModule: null,
  navProps: null,
  unmount: async () => {},
};

const state = {
  status: STATUS.NO_NAV_ROUTE,
  handle: null,
  // One async reconciliation at a time. Route/page events can arrive while
  // SystemJS imports or remounts are in flight, so later events request one
  // follow-up pass instead of starting overlapping mounts.
  reconcilePromise: null,
  pendingReconcile: false,
  observer: null,
  options: null,
  uiName: null,
  recoveryTimer: null,
  cleanupRegistered: false,
  mountAttempts: 0,
};

/**
 * Initialize navigation-v2 for a UI package.
 * @param {string} uiName - Name of the UI package (e.g., 'admin-ui')
 * @param {Object} [options] - Optional overrides for mountNavigationUiWithFallback
 * @param {Array<string|RegExp|Function>} [options.routeAllowlist] - Hash route matchers that should initialize nav-v2
 */
export function initNavigationV2(uiName, options = {}) {
  state.options = options;
  state.uiName = uiName;
  // Sticky states (FEATURE_SKIPPED, FAILED) suppress retries on route events
  // but should not block an explicit re-init call.
  if (state.status === STATUS.FEATURE_SKIPPED || state.status === STATUS.FAILED) {
    state.status = STATUS.NO_NAV_ROUTE;
    state.mountAttempts = 0;
  }
  registerCleanup();

  return reconcileNavigation();
}

export { mountNavigationUiWithFallback };

function matchesRoute(routeMatcher, hash) {
  if (typeof routeMatcher === 'string') return hash.startsWith(routeMatcher);
  if (routeMatcher instanceof RegExp) {
    // Reset lastIndex so a /g or /y regex doesn't alternate true/false across calls.
    routeMatcher.lastIndex = 0;
    return routeMatcher.test(hash);
  }
  if (typeof routeMatcher === 'function') return !!routeMatcher(hash);

  return false;
}

function isRouteAllowed(routeAllowlist = state.options?.routeAllowlist) {
  // No allowlist means historical behavior: this UI always wants navigation.
  if (!routeAllowlist) return true;
  if (typeof window === 'undefined') return true;
  return routeAllowlist.some(routeMatcher => matchesRoute(routeMatcher, window.location.hash));
}

function registerCleanup() {
  if (state.cleanupRegistered) return;
  state.cleanupRegistered = true;

  // Keep listeners active for the life of the document.
  // Unload-time teardown can fire during history transitions and leave nav unmounted on back.
  window.addEventListener('hashchange', onNavigationEvent);
  window.addEventListener('popstate', onNavigationEvent);
  window.addEventListener('pageshow', onNavigationEvent);
}

function enterNoNavRoute() {
  // A routeAllowlist miss means "show no nav", not "fall back to legacy nav".
  // Labs detail and PE public routes rely on this distinction.
  hideLegacyNavigation();
  state.status = STATUS.NO_NAV_ROUTE;
  state.mountAttempts = 0;

  // Preserve an already-mounted v2 handle. The owning Angular page can hide
  // the nav host with ng-hide, and returning to an allowed route can reuse it.
  if (!state.handle?.mounted) {
    state.handle = SKIPPED_HANDLE;
  }

  return state.handle;
}

// DOM-identity only — no layout cost. Safe to call on every mutation.
function hasStructuralProblem(targetElement) {
  const currentElement = state.handle?.navProps?.domElement;
  if (!targetElement) return !!currentElement;
  if (!currentElement) return true;
  if (targetElement !== currentElement) return true;
  if (!document.documentElement.contains(currentElement)) return true;
  return false;
}

function needsRecovery(targetElement) {
  if (hasStructuralProblem(targetElement)) return true;
  return !hasMountedNavContent(targetElement);
}

function reconcileNavigation() {
  if (state.reconcilePromise) {
    // Coalesce event storms. hashchange/popstate/pageshow and MutationObserver
    // callbacks can stack up during browser Back; one extra pass is enough.
    state.pendingReconcile = true;
    return state.reconcilePromise;
  }

  state.reconcilePromise = new Promise(resolve => resolve(reconcileNavigationOnce()))
    .catch(error => enterFailedState(error, 'reconcile failed'))
    .finally(() => {
      state.reconcilePromise = null;
      if (state.pendingReconcile) {
        state.pendingReconcile = false;
        reconcileNavigation();
      }
    });

  return state.reconcilePromise;
}

function reconcileNavigationOnce() {
  // Policy order matters:
  // 1. no-nav route => no nav at all
  // 2. mounted v2 => keep it and hide legacy
  // 3. feature skipped => keep legacy and avoid retry churn
  // 4. failed too many times => keep legacy
  // 5. otherwise try to mount v2
  if (!isRouteAllowed()) {
    return enterNoNavRoute();
  }

  if (state.handle?.mounted) {
    state.status = STATUS.MOUNTED;
    startObserver();
    const targetElement = document.getElementById(NAV_ID);
    if (!targetElement || needsRecovery(targetElement)) {
      // Handle may point to a detached or replaced node (e.g. Angular replaced
      // the mount element during a route transition). Schedule recovery rather
      // than returning the stale handle.
      scheduleRecoveryCheck();
    } else {
      hideLegacyNavigation();
    }
    return state.handle;
  }

  if (state.status === STATUS.FEATURE_SKIPPED) {
    showLegacyNavigation();
    return state.handle || SKIPPED_HANDLE;
  }

  if (state.mountAttempts >= MAX_MOUNT_ATTEMPTS) {
    if (state.status !== STATUS.FAILED) {
      state.status = STATUS.FAILED;
      showLegacyNavigation();
    }
    return state.handle || SKIPPED_HANDLE;
  }

  return mountNavigationV2();
}

function mountNavigationV2() {
  const mountOptions = { ...(state.options || {}) };
  // routeAllowlist is navigation-v2 policy, not a loader option. The loader
  // only knows how to mount/fallback once this module has decided v2 is wanted.
  delete mountOptions.routeAllowlist;

  state.status = STATUS.MOUNTING;
  state.mountAttempts += 1;

  return mountNavigationUiWithFallback({
    systemJsAmdUrl: null,
    logger: rollbar,
    shouldMount: () => featureToggles.isEnabled('dark-feature.navigation-v2'),
    onMountSuccess: () => console.info(`${state.uiName}: navigation-v2 mounted`), // eslint-disable-line no-console
    onMountSkipped: () => console.info(`${state.uiName}: navigation-v2 skipped`), // eslint-disable-line no-console
    ...mountOptions,
  }).then(handle => {
    state.handle = handle;

    if (handle?.mounted) {
      state.status = STATUS.MOUNTED;
      state.mountAttempts = 0;
      hideLegacyNavigation();
      startObserver();
      return handle;
    }

    // mounted=false with no error means the loader intentionally skipped,
    // normally because the navigation-v2 feature flag is off.
    state.status = handle?.error ? STATUS.FAILED : STATUS.FEATURE_SKIPPED;
    return handle;
  });
}

function enterFailedState(error, message) {
  state.status = STATUS.FAILED;
  state.handle = { ...SKIPPED_HANDLE, error };
  showLegacyNavigation();
  rollbar.error(`${state.uiName}: navigation-v2 ${message}`, error);
  return state.handle;
}

function attemptRecovery() {
  // Recovery only applies after a successful v2 mount. No-nav routes,
  // feature-skipped pages, and known failures are handled by reconciliation.
  if (state.status !== STATUS.MOUNTED || !state.handle?.mounted || state.reconcilePromise) return;
  if (!isRouteAllowed()) return;

  const targetElement = document.getElementById(NAV_ID);
  if (!needsRecovery(targetElement)) return;

  state.status = STATUS.RECOVERING;
  const preRecoveryHandle = state.handle;

  state.reconcilePromise = remountNavigationUi(preRecoveryHandle, targetElement)
    .then(nextHandle => {
      state.status = STATUS.MOUNTED;
      state.handle = nextHandle;
      hideLegacyNavigation();
      console.info(`${state.uiName}: navigation-v2 recovered`); // eslint-disable-line no-console
      return nextHandle;
    })
    .catch(error => {
      return enterFailedState(error, 'recovery failed');
    })
    .finally(() => {
      state.reconcilePromise = null;
      if (state.pendingReconcile) {
        state.pendingReconcile = false;
        reconcileNavigation();
      }
    });
}

function scheduleRecoveryCheck() {
  clearTimeout(state.recoveryTimer);
  state.recoveryTimer = setTimeout(attemptRecovery, RECOVERY_DELAY_MS);
}

function onNavigationEvent() {
  reconcileNavigation();
}

function startObserver() {
  if (state.observer) return;

  // body-wide because legacy code can manipulate shell DOM outside route
  // events.  #nav-v2 itself sits outside ng-view so route swaps won't
  // touch it, but this catches direct DOM rewrites and duplicate IDs.
  const observeRoot = document.body;
  if (!observeRoot) return;

  state.observer = new MutationObserver(() => {
    if (!isRouteAllowed()) {
      enterNoNavRoute();
      return;
    }

    // Structural only: body mutations can be noisy, and visibility checks use
    // getBoundingClientRect. Keep those checks in mount/reconciliation.
    if (hasStructuralProblem(document.getElementById(NAV_ID))) {
      scheduleRecoveryCheck();
    }
  });

  state.observer.observe(observeRoot, { childList: true, subtree: true });
}
